import axios from 'utils/axiosInstance';
import { useEffect, useState } from 'react';
import { Note, Tag } from 'types/notes';
import NotesIndex2 from './NotesIndex2';
import Banner2 from 'images/golden155/banner2.png';
import { useParams } from 'react-router';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import clsx from 'clsx';
import timeHelper from 'utils/timeHelper';
import { UserInfo } from 'types/user_info';
import { checkDailyLimit, addViewedNote } from 'utils/dailyViewTracker';
import CommentSection from 'components/comments/CommentSection';

// 무료 멤버십 가입 버튼 컴포넌트
const FreeMembershipJoinButton = () => {
  const [isLoading, setIsLoading] = useState(false);
  const VITE_DAILY_NOTE_VIEW_LIMIT = parseInt(import.meta.env.VITE_DAILY_NOTE_VIEW_LIMIT || '3');

  const handleFreeMembershipJoin = async () => {
    if (!confirm('무료 멤버십에 가입하시겠습니까?\n\n' +
      '- 클릭 한번으로 가입할 수 있습니다.\n' +
      '- 하루 ' + VITE_DAILY_NOTE_VIEW_LIMIT + '개의 노트를 읽을 수 있습니다.\n' +
      '- 언제든지 유료 멤버십으로 업그레이드 가능합니다.')) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post('/api/v1/plans/subscribe', {
        plan_key: 'basic'
      });

      if (response.data.success) {
        alert('무료 멤버십에 가입되었습니다!\n페이지를 새로고침합니다.');
        window.location.reload();
      } else {
        alert(response.data.message || '가입 중 오류가 발생했습니다.');
      }
    } catch (error: any) {
      console.error('무료 멤버십 가입 실패:', error);
      const message = error.response?.data?.message || '가입 중 오류가 발생했습니다.';
      alert(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleFreeMembershipJoin}
      disabled={isLoading}
      className="btn btn-primary w-full"
    >
      {isLoading ? (
        <>
          <span className="loading loading-spinner loading-sm"></span>
          가입 중...
        </>
      ) : (
        '무료 멤버십 바로 가입'
      )}
    </button>
  );
};

interface NoteShowProps {
  currentNoteId: number;
  userInfo: UserInfo | null;
  pboard?: string;
}

interface ScrapButtonProps {
  noteId: number;
  isLoggedIn: boolean;
}

// 스크랩 버튼 컴포넌트
const ScrapButton = ({ noteId, isLoggedIn }: ScrapButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isScrapped, setIsScrapped] = useState(false);

  // 로그인하지 않은 사용자에게는 아무것도 렌더링하지 않음
  if (!isLoggedIn) {
    return null;
  }

  useEffect(() => {
    if (isLoggedIn) {
      checkScrapStatus();
    }
  }, [noteId, isLoggedIn]);

  const checkScrapStatus = async () => {
    try {
      const response = await axios.get(`/api/v1/notes/${noteId}/scrap_status`);
      if (response.data.success) {
        setIsScrapped(response.data.is_scrapped);
      }
    } catch (error) {
      console.error('스크랩 상태 확인 실패:', error);
    }
  };

  const handleScrap = async () => {
    if (!isLoggedIn) {
      alert('로그인이 필요합니다.');
      return;
    }

    setIsLoading(true);

    try {
      const response = isScrapped
        ? await axios.delete(`/api/v1/notes/${noteId}/scrap`)
        : await axios.post(`/api/v1/notes/${noteId}/scrap`);

      if (response.data.success) {
        setIsScrapped(response.data.is_scrapped);
        alert(response.data.message);
      } else {
        alert(response.data.message);
      }
    } catch (error: any) {
      console.error('스크랩 처리 실패:', error);
      const message = error.response?.data?.message || '처리 중 오류가 발생했습니다.';
      alert(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex gap-2">
      <button
        onClick={handleScrap}
        disabled={isLoading}
        className={`btn btn-outline btn-sm ${isScrapped ? 'btn-success' : 'btn-primary'} flex-1`}
      >
        {isLoading ? (
          <>
            <span className="loading loading-spinner loading-sm"></span>
            처리중...
          </>
        ) : (
          <>
            <span className="text-lg">{isScrapped ? '📖' : '📝'}</span>
            {isScrapped ? '내 노트에서 빼기' : '내 노트에 넣기'}
          </>
        )}
      </button>

      <button
        onClick={() => window.location.href = '/profile/my_notes'}
        className="btn btn-outline btn-sm btn-ghost flex-none"
        title="내 노트 보기"
      >
        📚 내 노트
      </button>
    </div>
  );
};

// 태그 표시 컴포넌트
interface TagListProps {
  tags: Tag[];
  onTagClick: (tag: Tag) => void;
}

const TagList = ({ tags, onTagClick }: TagListProps) => {
  if (!tags || tags.length === 0) {
    return null;
  }

  return (
    <div className="px-4 sm:px-10 md:px-20 py-6">
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-600 font-semibold">태그:</span>
        {tags.map((tag) => (
          <button
            key={tag.id}
            onClick={() => onTagClick(tag)}
            className="px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-full text-sm font-medium transition-colors cursor-pointer"
          >
            #{tag.name}
          </button>
        ))}
      </div>
    </div>
  );
};

const NoteShow = ({ currentNoteId, userInfo, pboard }: NoteShowProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<Note | null>(null);
  const page = searchParams.get('page');
  const isAdmin = userInfo?.roleinfo?.isAllAdminGroup;
  const hasMembership = userInfo?.membership?.hasActiveMembership;
  const isLoggedIn = window.is_logged_in;

  const fetchNote = async (id: Number) => {
    setLoading(true);

    try {
      const { data } = await axios.get<Note>(`/api/v1/notes/${id}`);
      setNote(data);

      // 로그인했고, 성공적으로 조회되고 한도 초과가 아닌 경우에만 로컬 스토리지에 기록
      if (isLoggedIn && !(data as any).daily_limit_exceeded) {
        addViewedNote(id as number);
      }
    } catch (e: any) {
      console.error(e);
      // 에러 발생 시 기본 에러 처리
      setNote(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTagClick = (tag: Tag) => {
    // 태그 검색 페이지로 이동
    navigate(`/notes/${pboard || 'notes'}?tag=${tag.slug}`);
  };

  useEffect(() => {
    fetchNote(currentNoteId);
  }, [currentNoteId]);

  if (loading) {
    return (
      <div className='flex justify-center px-4 py-30'>
        <span className="loading loading-spinner sm:loading-sm lg:loading-lg"></span>
      </div>
    );
  }

  if (!note) {
    // note가 null인 경우 (데이터를 찾지 못했거나 아직 로딩되지 않은 상태)
    return <div className="flex justify-center px-4 py-30">노트를 찾을 수 없습니다.</div>;
  }

  return (
    <div>
      <div className="pt-10">
        <div className="sm:px-10 md:px-20">
          {/* <div className="flex justify-center px-4 pt-10 pb-20">
          <img src={Banner2} />
        </div> */}
          <div className="bg-black text-white text-sm font-bold py-1 px-3 inline-block mb-6">
            <Link to={`/notes/${note.pbid}`}>
              오늘의 {note.pboard_name === '황금노트' ? '황금노트' : (note.pboard_name || '황금노트').replace(/^황금/, '')}
            </Link>
          </div>

          <h1 className="text-3xl font-bold mb-4">
            {note.title}
          </h1>

          <div className="text-gray-500 text-sm mb-6 flex items-center space-x-2">
            {note.author && <span>{note.author}</span>}
            {/* <span className="px-2 py-0.5 bg-gray-200 rounded-full text-xs">K</span> */}
            {note.published_at &&
              <span>{timeHelper.timestr(note.published_at, 3)}</span>
            }
          </div>

          {/* <div className="flex items-center mb-6">
            <div className="avatar mr-4">
              <div className="w-12 rounded-full">
                <img src="https://longblack-contens.s3.ap-northeast-2.amazonaws.com/image/20250513/17471349739e8520f7a49c77aa212a1c240118177b.png" alt="name Avatar" />
              </div>
            </div>
            <div>
              <div className="text-lg font-semibold">{note.author}</div>
            </div>
          </div> */}
        </div>

        <div className='content-wrap relative px-0 py-10 sm:px-10 md:px-20 sm:py-20'>
          <div className={clsx("prose max-w-none",
            (!window.is_logged_in || !hasMembership || (note as any).daily_limit_exceeded) ? "max-h-[4000px] overflow-y-hidden" : "")}
            dangerouslySetInnerHTML={{ __html: note.content }}>
          </div>
          {/* <!-- pointer-events-none --> */}
          {!window.is_logged_in ? (
            <>
              <div className='fader'></div>
              <div className=' w-full z-[1000]'>
                <div className='flex justify-center px-4 pt-5 py-10'>
                  <div>
                    <div className='text-center text-2xl font-bold'>지금 가입하고 <br />인사이트를 쌓아보세요.</div>
                    <br />
                    <a href='/signup_check' className="btn btn-primary w-full">회원 가입하기</a>
                  </div>
                </div>
                {/* <p className='flex justify-center w-full text-xs'>※ 멤버십 회원만 모든 황금노트를 확인할 수 있습니다. 무료회원으로 가입해보세요.(하루 조회수 제한있음)</p> */}
              </div>
            </>
          ) : window.is_logged_in && !hasMembership ? (
            <>
              <div className='fader'></div>
              <div className=' w-full z-[1000]'>
                <div className='flex justify-center px-4 pt-5 py-10'>
                  <div className="w-full max-w-sm">
                    <div className='text-center text-2xl font-bold'>멤버십 구독으로 <br />모든 노트를 확인하세요.</div>
                    <br />
                    <div className="space-y-3">
                      <FreeMembershipJoinButton />
                      {/* <a href='/select_plan' className="btn btn-outline btn-primary w-full">유료 멤버십 업그레이드</a> */}
                    </div>
                  </div>
                </div>
                {/* <p className='flex justify-center w-full text-xs'>※ 멤버십 회원이 되면 모든 황금노트를 확인할 수 있습니다.</p> */}
              </div>
            </>
          ) : (note as any).daily_limit_exceeded ? (
            <>
              <div className='fader'></div>
              <div className=' w-full z-[1000]'>
                <div className='flex justify-center px-4 pt-5 py-10'>
                  <div className="w-full max-w-sm">
                    <div className='text-center text-2xl font-bold'>일일 조회 한도 초과</div>
                    <br />
                    <div className='text-center text-lg mb-4'>
                      무료 멤버십(준회원)은 하루에 {(note as any).limit}개의 노트만 조회할 수 있습니다.
                    </div>
                    <div className="text-center text-sm text-gray-600 mb-6">
                      오늘 조회한 노트: {(note as any).current_count}/{(note as any).limit}개
                    </div>
                    {/* <a href='/select_plan' className="btn btn-primary w-full">유료 멤버십 업그레이드</a> */}
                  </div>
                </div>
                <p className='flex justify-center w-full text-xs'>※ 실버/골드 멤버십으로 업그레이드하면 제한없이 조회가 가능합니다.</p>
              </div>
            </>
          ) : null}
        </div>

        {/* 태그 목록 */}
        {note.tags && note.tags.length > 0 && (
          <div className='mb-6'>
            <TagList tags={note.tags} onTagClick={handleTagClick} />
          </div>
        )}

        {/* 스크랩 버튼 (로그인한 사용자만) */}
        {isLoggedIn && (
          <div className="px-4 sm:px-10 md:px-20 pt-10 pb-6 mb-10">
            <div className="min-w-[250px] max-w-[320px] md:max-w-[350px] mx-auto">
              <ScrapButton noteId={currentNoteId} isLoggedIn={isLoggedIn} />
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="float-right text-right mt-[-20px]">
            <div className='w-full flex justify-end max-h-16 overflow-hidden'>
              <a href={`/admin/pposts?pbid=${note.pbid}`}><img src="/img/common/admin.png" /></a>&nbsp;
              <a href={`/admin/pposts/${currentNoteId}/edit`}><img src="/img/common/admin.png" /></a>&nbsp;
            </div>
          </div>
        )
        }

        <div className="px-4 sm:px-10 md:px-20 pb-10">
          <CommentSection noteId={currentNoteId} isLoggedIn={isLoggedIn} userInfo={userInfo} />
        </div>

        <div className="pb-10 px-0 sm:px-4">
          <NotesIndex2 currentNoteId={currentNoteId} page={parseInt(page || '1')} />
        </div>
      </div>
    </div>
  );
}

export default NoteShow;
